<script setup>
import { computed, ref } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';

const props = defineProps({
  agentList: {
    type: Array,
    default: () => [],
  },
  selectedAgents: {
    type: Array,
    default: () => [],
  },
  updateSelectedAgents: {
    type: Function,
    default: () => {},
  },
  // Map { agentId -> role }
  agentRoles: {
    type: Object,
    default: () => ({}),
  },
  updateAgentRole: {
    type: Function,
    default: () => {},
  },
  isWorking: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const getters = useStoreGetters();

const currentRole = computed(() => getters.getCurrentRole.value);
const isAdmin = computed(() => currentRole.value === 'administrator');

// Permisos del usuario actual para decidir qué roles puede asignar
// a los miembros del equipo.
const currentUserPermissions = computed(() => {
  const user = getters.getCurrentUser.value;
  const accountId = getters.getCurrentAccountId.value;
  const account = (user.accounts || []).find(
    a => Number(a.id) === Number(accountId)
  );
  return (account && account.permissions) || [];
});

const isCoordinator = computed(() =>
  currentUserPermissions.value.includes('team_coordinator_manage')
);
const isSupervisor = computed(() =>
  currentUserPermissions.value.includes('team_supervisor_manage')
);

// Para el Responsable, el dropdown de roles se oculta — solo añade/quita
// miembros, no cambia roles. Para Coord puede cambiar a member/supervisor.
// Admin puede asignar también coordinator.
const canChangeMemberRoles = computed(
  () => isAdmin.value || isCoordinator.value
);

const searchQuery = ref('');

// Lista filtrada por la búsqueda. Coincide con name o email (case-insensitive).
const filteredAgentList = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return props.agentList;
  return props.agentList.filter(a => {
    const name = (a.name || '').toLowerCase();
    const email = (a.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });
});

const availableRoles = computed(() => {
  // Responsable (supervisor): el dropdown se oculta. Solo puede añadir como
  // 'member' implícito (el backend fuerza 'member' si no es Coord/Admin).
  if (isSupervisor.value && !isAdmin.value && !isCoordinator.value) {
    return [{ value: 'member', label: t('TEAMS_SETTINGS.AGENTS.ROLE.MEMBER') }];
  }
  const roles = [
    { value: 'member', label: t('TEAMS_SETTINGS.AGENTS.ROLE.MEMBER') },
    { value: 'supervisor', label: t('TEAMS_SETTINGS.AGENTS.ROLE.SUPERVISOR') },
  ];
  if (isAdmin.value) {
    roles.push({
      value: 'coordinator',
      label: t('TEAMS_SETTINGS.AGENTS.ROLE.COORDINATOR'),
    });
  }
  return roles;
});

const selectedAgentCount = computed(() => props.selectedAgents.length);

// "Seleccionar todos" actúa sobre los visibles (los filtrados).
// Se considera "todos seleccionados" cuando todos los visibles están seleccionados.
const visibleIds = computed(() => filteredAgentList.value.map(a => a.id));

const allVisibleSelected = computed(
  () =>
    visibleIds.value.length > 0 &&
    visibleIds.value.every(id => props.selectedAgents.includes(id))
);

const someVisibleSelected = computed(() => {
  const someSelected = visibleIds.value.some(id =>
    props.selectedAgents.includes(id)
  );
  return someSelected && !allVisibleSelected.value;
});

const disableSubmitButton = computed(() => selectedAgentCount.value === 0);

const isAgentSelected = agentId => {
  return props.selectedAgents.includes(agentId);
};

const getRoleForAgent = agentId => {
  return props.agentRoles[agentId] || 'member';
};

// Roles por encima de lo que el actor puede asignar: esas filas no se pueden
// quitar ni cambiar de rol (refleja la restricción del backend, que ignora
// los borrados de miembros con rol superior al asignable por el actor).
const lockedRoles = computed(() => {
  if (isAdmin.value) return [];
  if (isCoordinator.value) return ['coordinator'];
  return ['supervisor', 'coordinator'];
});

const isAgentLocked = agentId =>
  isAgentSelected(agentId) &&
  lockedRoles.value.includes(getRoleForAgent(agentId));

// Particiones de la lista visible: los seleccionados se muestran SIEMPRE al
// principio de la lista, el resto debajo.
const selectedVisibleAgents = computed(() =>
  filteredAgentList.value.filter(a => isAgentSelected(a.id))
);
const unselectedVisibleAgents = computed(() =>
  filteredAgentList.value.filter(a => !isAgentSelected(a.id))
);

const handleSelectAgent = agentId => {
  const shouldRemove = isAgentSelected(agentId);
  if (shouldRemove && isAgentLocked(agentId)) {
    // eslint-disable-next-line no-console
    console.log(
      `[AgentSelector] ignored deselect of locked agent=${agentId} role=${getRoleForAgent(agentId)}`
    );
    return;
  }

  let result = [];
  if (shouldRemove) {
    result = props.selectedAgents.filter(item => item !== agentId);
  } else {
    result = [...props.selectedAgents, agentId];
  }

  props.updateSelectedAgents(result);
  if (!shouldRemove && !props.agentRoles[agentId]) {
    props.updateAgentRole(agentId, 'member');
  }
};

const handleRoleChange = (agentId, newRole) => {
  // eslint-disable-next-line no-console
  console.log(`[AgentSelector] role change agent=${agentId} → ${newRole}`);
  props.updateAgentRole(agentId, newRole);
};

// Etiqueta del rol actual para mostrar en el botón del SelectMenu.
const roleLabel = agentId => {
  const role = getRoleForAgent(agentId);
  return (
    availableRoles.value.find(r => r.value === role)?.label ||
    t(`TEAMS_SETTINGS.AGENTS.ROLE.${role.toUpperCase()}`)
  );
};

const toggleSelectAll = () => {
  if (allVisibleSelected.value) {
    // Deseleccionar SOLO los visibles, conservando las filas bloqueadas
    const remaining = props.selectedAgents.filter(
      id => !visibleIds.value.includes(id) || isAgentLocked(id)
    );
    props.updateSelectedAgents(remaining);
  } else {
    // Añadir los visibles que aún no estén seleccionados
    const toAdd = visibleIds.value.filter(
      id => !props.selectedAgents.includes(id)
    );
    const result = [...props.selectedAgents, ...toAdd];
    props.updateSelectedAgents(result);
    toAdd.forEach(id => {
      if (!props.agentRoles[id]) props.updateAgentRole(id, 'member');
    });
  }
};

const clearSearch = () => {
  searchQuery.value = '';
};
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Search bar -->
    <div class="relative mb-3 flex-shrink-0">
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-xl border border-n-weak bg-n-alpha-1 transition-colors focus-within:border-n-brand focus-within:bg-n-background"
      >
        <Icon icon="i-lucide-search" class="text-n-slate-10 size-4" />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('TEAMS_SETTINGS.AGENTS.SEARCH_PLACEHOLDER')"
          class="bg-transparent w-full outline-none text-body-main text-n-slate-12 placeholder:text-n-slate-10"
        />
        <span
          v-if="selectedAgentCount"
          class="flex-shrink-0 rounded-full bg-n-brand/10 text-n-brand px-2 py-0.5 text-body-small font-medium"
        >
          {{ selectedAgentCount }}
        </span>
        <button
          v-if="searchQuery"
          type="button"
          class="text-n-slate-10 hover:text-n-slate-12"
          :title="$t('TEAMS_SETTINGS.AGENTS.CLEAR_SEARCH')"
          @click="clearSearch"
        >
          <Icon icon="i-lucide-x" class="size-4" />
        </button>
      </div>
      <p
        v-if="searchQuery && filteredAgentList.length === 0"
        class="text-body-small text-n-slate-10 mt-2"
      >
        {{ $t('TEAMS_SETTINGS.AGENTS.NO_RESULTS') }}
      </p>
    </div>

    <!-- Scrollable agents list: [seleccionados primero, resto debajo] -->
    <div
      class="flex-1 min-h-0 overflow-y-auto border border-n-weak rounded-xl bg-n-background"
    >
      <!-- Sticky header: select-all + column labels -->
      <div
        class="sticky top-0 z-10 flex items-center gap-3 px-4 py-2.5 bg-n-background border-b border-n-weak"
      >
        <Checkbox
          :model-value="allVisibleSelected"
          :indeterminate="someVisibleSelected"
          :title="$t('TEAMS_SETTINGS.AGENTS.SELECT_ALL')"
          @change="toggleSelectAll"
        />
        <span
          class="text-body-small font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ $t('TEAMS_SETTINGS.AGENTS.AGENT') }}
        </span>
        <span
          class="ms-auto text-body-small font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ $t('TEAMS_SETTINGS.AGENTS.ROLE.HEADER') }}
        </span>
      </div>

      <template
        v-for="section in [
          {
            key: 'selected',
            label: $t('TEAMS_SETTINGS.AGENTS.SELECTED_SECTION', {
              count: selectedVisibleAgents.length,
            }),
            agents: selectedVisibleAgents,
          },
          {
            key: 'rest',
            label: $t('TEAMS_SETTINGS.AGENTS.REST_SECTION'),
            agents: unselectedVisibleAgents,
          },
        ]"
        :key="section.key"
      >
        <template v-if="section.agents.length">
          <div
            class="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10 bg-n-alpha-1 border-b border-n-weak"
          >
            {{ section.label }}
          </div>

          <div
            v-for="agent in section.agents"
            :key="agent.id"
            class="group flex items-center gap-3 px-4 py-2.5 border-b border-n-weak last:border-b-0 transition-colors"
            :class="[
              isAgentLocked(agent.id)
                ? 'opacity-75 cursor-not-allowed'
                : 'cursor-pointer hover:bg-n-alpha-1',
              isAgentSelected(agent.id) ? 'bg-n-alpha-1' : '',
            ]"
            @click="handleSelectAgent(agent.id)"
          >
            <Checkbox
              :model-value="isAgentSelected(agent.id)"
              :disabled="isAgentLocked(agent.id)"
              @click.stop
              @change="() => handleSelectAgent(agent.id)"
            />

            <Avatar
              :src="agent.thumbnail"
              :name="agent.name"
              :status="agent.availability_status"
              :size="32"
              hide-offline-status
              rounded-full
              class="flex-shrink-0"
            />

            <div class="flex flex-col min-w-0">
              <span class="text-heading-3 text-n-slate-12 truncate">
                {{ agent.name }}
              </span>
              <span class="text-body-small text-n-slate-10 truncate">
                {{ agent.email || '---' }}
              </span>
            </div>

            <div class="ms-auto flex-shrink-0 max-w-44" @click.stop>
              <template v-if="isAgentSelected(agent.id)">
                <SelectMenu
                  v-if="canChangeMemberRoles && !isAgentLocked(agent.id)"
                  :model-value="getRoleForAgent(agent.id)"
                  :options="availableRoles"
                  :label="roleLabel(agent.id)"
                  sub-menu-position="bottom"
                  @update:model-value="
                    value => handleRoleChange(agent.id, value)
                  "
                />
                <span
                  v-else
                  class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2.5 py-1 text-body-small text-n-slate-11"
                  :title="
                    $t('TEAMS_SETTINGS.AGENTS.ROLE.LOCKED_FOR_SUPERVISOR')
                  "
                >
                  <Icon icon="i-lucide-lock" class="size-3" />
                  {{
                    $t(
                      `TEAMS_SETTINGS.AGENTS.ROLE.${getRoleForAgent(
                        agent.id
                      ).toUpperCase()}`
                    )
                  }}
                </span>
              </template>
              <span v-else class="text-body-main text-n-slate-8">—</span>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Sticky footer with count + submit -->
    <div
      class="flex items-center justify-between mt-4 pt-3 border-t border-n-weak flex-shrink-0 bg-n-background"
    >
      <p class="text-body-main text-n-slate-11">
        {{
          $t('TEAMS_SETTINGS.AGENTS.SELECTED_COUNT', {
            selected: selectedAgents.length,
            total: agentList.length,
          })
        }}
      </p>
      <NextButton
        type="submit"
        :label="submitButtonText"
        :disabled="disableSubmitButton"
        :is-loading="isWorking"
      />
    </div>
  </div>
</template>
